import React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

/**
 * MUI rebuild of the reference (shadcn/Tailwind) AccessDeniedCard — same props,
 * same layout/behavior, ported for etmf-client's Material-UI-based architecture.
 */

const BRAND_GRADIENT = 'linear-gradient(135deg, #168AAD 0%, #76C893 100%)';
const BRAND_GRADIENT_STRONG = 'linear-gradient(135deg, #168AAD 0%, #0E5871 100%)';

/** Primary filled action — brand gradient, matches the navbar identity. */
const ACTION_PRIMARY_SX = {
  color: '#fff',
  border: '1px solid transparent',
  background: 'linear-gradient(135deg, #168AAD 0%, #127190 100%)',
  boxShadow: '0 10px 25px -8px rgba(22,138,173,0.45)',
  '&:hover': { filter: 'brightness(1.04)', boxShadow: '0 12px 28px -8px rgba(22,138,173,0.55)' },
  '&:active': { filter: 'brightness(0.95)' },
};

/** Outline secondary — theme-aware slate border + label on card surface. */
const actionSecondarySx = (theme) => ({
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#E2E8F0',
  color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#334155',
  bgcolor: theme.palette.mode === 'dark' ? 'transparent' : '#fff',
  '&:hover': {
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.24)' : '#CBD5E1',
  },
  '&:active': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#F1F5F9' },
  '&.Mui-disabled': { opacity: 0.6 },
});

/**
 * Access denied alert (centered, elevated layout): gradient icon badge, eyebrow,
 * bold title, reason line, guidance box, then Refresh + Request actions. Styled
 * to read as a deliberate compliance boundary on a regulated clinical platform —
 * a "this is by design" screen, not a broken-page error.
 */
export default function AccessDeniedCard({
  title = "You don't have access",
  /** Second line under title (policy reason, study context, etc.) */
  subtitle,
  /** @deprecated Prefer `subtitle`; still supported */
  description,
  children,
  className,
  contentClassName,
  /** Used when subtitle/description would otherwise be blank */
  descriptionFallback,
  hideDescriptionWhenEmpty = false,
  /** @deprecated — alias of hideDescriptionWhenEmpty */
  hideSubtitleWhenEmpty,
  requestAccessHref,
  requestAccessLabel = 'Request for Access',
  requestAccessDisabled = false,
  onRefresh,
  refreshLabel = 'Refresh page',
  showRefresh = false,
  /** Small uppercase label above the title. */
  eyebrow = 'Restricted access',
  sx: sxProp,
  ...cardProps
}) {
  const hideWhenEmpty =
    hideSubtitleWhenEmpty !== undefined ? hideSubtitleWhenEmpty : hideDescriptionWhenEmpty;

  const rawSubtitle =
    subtitle != null && String(subtitle).trim() !== ''
      ? String(subtitle).trim()
      : typeof description === 'string' && description.trim() !== ''
        ? description.trim()
        : '';

  const subtitleShown =
    !hideWhenEmpty || Boolean(rawSubtitle) || Boolean(descriptionFallback);
  const subtitleText =
    rawSubtitle ||
    (descriptionFallback && String(descriptionFallback).trim()) ||
    '';

  return (
    <Card
      {...cardProps}
      className={className}
      sx={[
        (theme) => ({
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(226,232,240,0.7)',
          bgcolor: theme.palette.mode === 'dark' ? '#171E26' : '#fff',
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'none'
              : '0 1px 2px rgba(15,23,42,0.04), 0 24px 48px -12px rgba(15,23,42,0.16)',
        }),
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    >
      {/* Faint watermark lock — premium-SaaS texture, not decoration for its own sake. */}
      <LockOutlinedIcon
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          right: -24,
          top: -24,
          fontSize: 128,
          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
          transform: 'rotate(14deg)',
          pointerEvents: 'none',
        })}
      />

      <CardContent
        className={contentClassName}
        sx={{ position: 'relative', p: { xs: 3, sm: 3.5 }, textAlign: 'center' }}
      >
        <Box sx={{ position: 'relative', mx: 'auto', display: 'flex', height: 56, width: 56, alignItems: 'center', justifyContent: 'center' }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '16px',
              transform: 'rotate(6deg)',
              background: BRAND_GRADIENT,
              opacity: 0.14,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              height: 48,
              width: 48,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '16px',
              boxShadow: '0 10px 25px -5px rgba(22,138,173,0.35)',
              background: BRAND_GRADIENT_STRONG,
            }}
          >
            <GppMaybeOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} aria-hidden />
          </Box>
        </Box>

        <Box sx={{ mx: 'auto', mt: 2, maxWidth: 380 }}>
          {eyebrow ? (
            <Typography
              sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#127190' }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          {title ? (
            <Typography
              variant="h6"
              component="h2"
              sx={{ mt: 0.5, fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em', color: 'text.primary' }}
            >
              {title}
            </Typography>
          ) : null}
          {subtitleShown && subtitleText ? (
            <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', lineHeight: 1.6 }}>
              {subtitleText}
            </Typography>
          ) : null}
        </Box>

        {children ? (
          <Box
            sx={(theme) => ({
              mx: 'auto',
              mt: 2.5,
              maxWidth: 380,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F1F5F9',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.7)',
              p: 2,
              textAlign: 'left',
            })}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.6 }}>
              {children}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
          {showRefresh && typeof onRefresh === 'function' ? (
            <Button
              type="button"
              size="small"
              onClick={onRefresh}
              startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
              sx={{ ...ACTION_PRIMARY_SX, height: 36, px: 2 }}
            >
              {refreshLabel}
            </Button>
          ) : null}

          {requestAccessHref && !requestAccessDisabled ? (
            <Button
              variant="outlined"
              size="small"
              component="a"
              href={requestAccessHref}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<ArrowOutwardIcon sx={{ fontSize: 14 }} />}
              sx={(theme) => ({ ...actionSecondarySx(theme), height: 36, px: 2 })}
            >
              {requestAccessLabel}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={requestAccessDisabled}
              sx={(theme) => ({ ...actionSecondarySx(theme), height: 36, px: 2 })}
            >
              {requestAccessLabel}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

AccessDeniedCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  descriptionFallback: PropTypes.string,
  hideDescriptionWhenEmpty: PropTypes.bool,
  hideSubtitleWhenEmpty: PropTypes.bool,
  requestAccessHref: PropTypes.string,
  requestAccessLabel: PropTypes.string,
  requestAccessDisabled: PropTypes.bool,
  onRefresh: PropTypes.func,
  refreshLabel: PropTypes.string,
  showRefresh: PropTypes.bool,
  eyebrow: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};
